import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { techSearchParams } from '#/service/utils';

// uncomment the console.log here to get debug info about all the redirections.
const debugLog = (_debugInfo?: string, ..._params: any[]) => {
  // console.log('useNavigation:', _debugInfo, '->', ..._params);
};

export interface UseNavigationResult {
  goBack: () => void;
  goHome: () => void;

  /**
   * Method used for navigating between route states
   * @param path {string} The path to navigate to.
   * @param state {Object} The state to pass to the new route.
   * Please do not put any sensitive data in here.
   * Also do not put too much data here as it may affect performance,
   * because state is often serialized and deserialized
   */
  go: (path: string, state?: Object) => void;
  reloadPage: () => void;
}

/**
 * Common navigation actions.
 * @param debugInfo {string} Info about what component is using navigation and why. For debugging.
 * @returns {UseNavigationResult} Common navigation functions.
 */
export const useNavigation = (debugInfo: string): UseNavigationResult => {
  const navigate = useNavigate();
  const location = useLocation();

  const goHome = useCallback(() => {
    debugLog(debugInfo, 'goHome');

    // if already at home, just scroll to top
    if (location.pathname === '/') {
      window.scrollTo(0, 0);
    } else {
      navigate({
        pathname: `/`,
        search: techSearchParams(location.search),
      });
    }
  }, [navigate, location, debugInfo]);

  const goBack = useCallback(() => {
    debugLog(debugInfo, 'goBack');

    // if the page was deeplinked, go back to the home page
    // note: first entry is almost always the new tab/window
    if (window.history.length < 3) {
      goHome();
    } else {
      navigate(-1);
    }
  }, [navigate, goHome, debugInfo]);

  const go = useCallback(
    (path: string, state?: Object) => {
      debugLog(debugInfo, 'go');
      navigate(
        {
          pathname: path,
          search: techSearchParams(location.search),
        },
        {
          state,
        }
      );
    },
    [navigate, debugInfo, location]
  );

  const reloadPage = useCallback(() => {
    debugLog(debugInfo, 'reloadPage');
    navigate(0);
  }, [navigate, debugInfo]);

  return {
    goHome,
    goBack,
    go,
    reloadPage,
  };
};
